import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ScanIcon } from '../../components/ui/icons';
import { useAnchoredList } from '../../components/ui/useAnchoredList';
import { num } from '../../format';
import * as scanner from '../../platform/scanner';
import { useScanTarget } from '../../platform/scanning';
import { resolveScan } from '../scan/api';
import { ScanSheet } from '../scan/ScanSheet';
import { knownLocations } from '../stock/api';
import { getLocation, searchLocations } from './api';

interface Props {
  label: string;
  value: string | null;
  onChange: (id: string | null) => void;
  /** Whether "outside the warehouse" is a legitimate answer. It is when posting
   *  a movement — a write-off leaves the building — and it is not when putting a
   *  parcel away, which by definition lands somewhere. */
  allowOutside?: boolean;
  /** When given, the places this part has lived in are offered as one press
   *  each. A part almost always goes back where it came from, so searching for
   *  that drawer again every time is work the system already knows the answer to. */
  componentId?: string | null;
  /** Whether a scanned code should land in this field. Set on the field being
   *  filled in right now: a receiving form has one of these per line, and only
   *  one of them is the one the person is standing at. */
  scanTarget?: boolean;
  /** Allows the current form to route a component QR without showing a place error. */
  onComponentScan?: (componentId: string) => void;
  /** Told when this field is the one being used, so a form with several of them
   *  can decide which is the scan target. */
  onFocus?: () => void;
}

/**
 * Search rather than a dropdown: the tree runs to hundreds of nodes and names
 * repeat ("Полка 3" exists in every cabinet), so the ancestor chain is the only
 * thing that makes a choice unambiguous.
 *
 * And a scanner: the label on the shelf holds the code of that shelf, so putting
 * a parcel away is "read the box, read the shelf" rather than "read the box,
 * then type the name of the shelf you are standing in front of". A code goes
 * through `/resolve`, which accepts our short code, the whole URL a 2-D imager
 * reads off the QR, and lower case — so what the gun sends is never the problem.
 */
export function LocationPicker({
  label,
  value,
  onChange,
  allowOutside = true,
  componentId = null,
  scanTarget = false,
  onComponentScan,
  onFocus,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [camera, setCamera] = useState(false);
  const [focused, setFocused] = useState(false);
  const anchor = useRef<HTMLInputElement>(null);

  const known = useQuery({
    queryKey: ['stock', 'known-locations', componentId],
    queryFn: () => knownLocations(componentId as string),
    enabled: Boolean(componentId),
  });

  const results = useQuery({
    queryKey: ['locations', 'search', query],
    queryFn: () => searchLocations(query),
    enabled: query.length > 0,
  });

  // A value can arrive already chosen — a purchase line suggesting where its
  // parcel should land — and then no search has been run to name it. Without
  // this the field looked empty while a place was in fact selected.
  const preset = useQuery({
    queryKey: ['locations', 'detail', value],
    queryFn: () => getLocation(value as string),
    enabled: Boolean(value),
  });

  const chosen =
    results.data?.find((row) => row.id === value)?.name ??
    (preset.data?.id === value ? preset.data.name : undefined);

  const suggestions = value || query ? [] : (known.data ?? []);

  // Positioned against the input rather than nested in it: every pane here
  // scrolls, and a nested list is clipped by the first one it meets.
  const open = focused && Boolean(query) && !value;
  const position = useAnchoredList(anchor, open);

  /** A code from any reader. Anything that is not a place is refused out loud:
   *  silently ignoring a part's label here would look like a broken scanner. */
  const accept = async (code: string) => {
    setReading(true);
    setScanError(null);
    try {
      const found = await resolveScan(code);
      if (found.resolved && found.match?.kind === 'location') {
        onChange(found.match.id);
        setQuery(found.match.title);
      } else if (found.resolved && found.match?.kind === 'component' && onComponentScan) {
        onComponentScan(found.match.id);
        setQuery('');
      } else {
        setScanError(found.resolved ? 'notALocation' : 'unknownCode');
      }
    } catch {
      setScanError('unknownCode');
    } finally {
      setReading(false);
    }
  };

  useScanTarget((code) => void accept(code), scanTarget);

  const cameraReady = scanner.cameraSupport() === 'ok';

  return (
    <span
      className="stock__picker"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <span className="stock__picker-field">
        <input
          ref={anchor}
          value={chosen ?? query}
          onChange={(event) => {
            setQuery(event.target.value);
            setScanError(null);
            onChange(null);
          }}
          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onKeyDown={(event) => {
            // A gun in keyboard mode types into whatever has the focus and then
            // presses Enter. Here that is this field, so this is where the code
            // has to be recognised — the global listener deliberately keeps out
            // of text fields.
            if (event.key !== 'Enter') return;
            const typed = (chosen ?? query).trim();
            if (!typed) return;
            event.preventDefault();
            void accept(typed);
          }}
          placeholder={label}
          aria-label={label}
        />
        {cameraReady && (
          <button
            type="button"
            className="stock__picker-scan"
            title={t('stock.scanPlace')}
            aria-label={t('stock.scanPlace')}
            onClick={() => setCamera(true)}
          >
            <ScanIcon />
          </button>
        )}
      </span>
      {camera && (
        <ScanSheet
          title={t('stock.scanPlace')}
          onRead={(code) => {
            setCamera(false);
            void accept(code);
          }}
          onCancel={() => setCamera(false)}
        />
      )}
      {reading && !scanError && <span className="hint">{t('common.loading')}</span>}
      {scanError && <span className="error">{t(`stock.${scanError}`)}</span>}
      {suggestions.length > 0 && (
        <span className="stock__known">
          {suggestions.map((place) => {
            // Places this part has lived in, empty ones included: a parcel of it
            // usually goes back where the last one came from, and that drawer is
            // empty precisely when a new parcel turns up. But an empty one used
            // to look exactly like a full one — the count was simply left off —
            // so it now says it is empty rather than saying nothing.
            const empty = Number(place.qty) === 0;
            return (
              <button
                key={place.location.id}
                type="button"
                className={empty ? 'stock__known-place is-empty' : 'stock__known-place'}
                title={[...place.ancestors, place.location.name].join(' → ')}
                onClick={() => {
                  onChange(place.location.id);
                  setQuery(place.location.name);
                }}
              >
                {place.location.name}
                <em> {empty ? t('stock.emptyNow') : num(place.qty)}</em>
              </button>
            );
          })}
        </span>
      )}
      {open && (
        <ul className="stock__picker-results" style={position}>
          {(results.data ?? []).map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(row.id);
                  setQuery(row.name);
                }}
              >
                {row.name}
                {row.ancestors.length > 0 && <em> ({row.ancestors.join(' → ')})</em>}
              </button>
            </li>
          ))}
          {allowOutside && (
            <li>
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setQuery('');
                }}
              >
                {t('stock.outside')}
              </button>
            </li>
          )}
        </ul>
      )}
    </span>
  );
}
